import { useEffect } from 'react';
import Head from 'next/head';

import { ListingDetail } from '@components/programme/ListingDetail';
import { Seo } from '@components/seo/Seo';

import { serverSideWithSiteData } from '@common/utils/page';
import { getEventMetaDescription, getEventUrl } from '@common/utils/programme';
import { queryFromDato } from '@data/datocms/client';
import { programmeDetail } from '@data/datocms/queries/programme';
import { legacyDiceSlugs } from '@data/legacyDiceSlugs';
import { supabaseAdminClient } from '@data/supabase/admin';
import { getEvent } from '@data/supabase/getEvent';
import { getEventList } from '@data/supabase/getEventList';
import {
	ProgrammeDetailResponse,
	ProgrammeSpacePreviewResponse,
	SiteData,
	SpacePreviewData,
	SupabaseEventRecord,
} from '@interfaces';

interface EventDetailPageProps extends ProgrammeDetailResponse {
	space?: SpacePreviewData;
	event: SupabaseEventRecord;
	relatedEvents: SupabaseEventRecord[];
}

const EventDetailPage = ({
	event,
	site,
	programme,
	relatedEvents,
	space,
}: EventDetailPageProps & SiteData) => {
	const url = getEventUrl({ event });
	useEffect(() => {
		if (window.clarity) {
			window.clarity('event', 'viewEvent');
		}
	}, []);

	return (
		<>
			<Head>
				<link href={url.href} rel="canonical" />
			</Head>
			<Seo
				metaTagData={[
					...site.faviconMetaTags,
					{
						__typename: 'Tag',
						tag: 'title',
						content: event.name,
						attributes: {},
					},
					{
						__typename: 'Tag',
						tag: 'meta',
						content: null,
						attributes: {
							name: 'description',
							content: getEventMetaDescription(event),
						},
					},
				]}
			/>
			<ListingDetail
				event={event}
				programmeData={programme}
				relatedEvents={relatedEvents}
				space={space}
			/>
		</>
	);
};

export const getServerSideProps = serverSideWithSiteData<EventDetailPageProps>(
	async ({ params = {} }) => {
		if (typeof params.slug !== 'string') {
			return {
				notFound: true,
			};
		}

		if (Number.isNaN(Number(params.slug))) {
			// If the slug is non-numeric it might be a legacy Dice slug, look up the slug to use:
			const slug = legacyDiceSlugs.get(params.slug);

			if (slug) {
				// Redirect to the new slug if one is found:
				return {
					redirect: {
						destination: `/events/${slug}`,
						permanent: true,
					},
				};
			}
		}

		const event = await getEvent({
			options: {
				elsewherePresents: false,
				excludePast: false,
				slug: params.slug,
			},
			supabaseClient: supabaseAdminClient,
		});

		if (!event) {
			return {
				notFound: true,
			};
		}

		// The amount of related events slots
		let relatedEventSlots = 3;

		// Prepare related events by type
		let relatedEventsByType: SupabaseEventRecord[] = [];

		// Get related events by genre_tag
		const { events: relatedEventsByGenre } = await getEventList({
			options: {
				elsewherePresents: false,
				excludeDice: true,
				genres: event.genres,
				range: {
					from: 0,
					to: relatedEventSlots - 1,
				},
			},
			supabaseClient: supabaseAdminClient,
		});

		relatedEventSlots -= relatedEventsByGenre.length;

		// If not all related event slots are populated try adding more events by type
		if (relatedEventSlots && event.type) {
			const eventList = await getEventList({
				options: {
					elsewherePresents: false,
					excludeDice: true,
					excludeIds: relatedEventsByGenre.map(({ id }) => id),
					range: {
						from: 0,
						to: relatedEventSlots - 1,
					},
					type: event.type,
				},
				supabaseClient: supabaseAdminClient,
			});

			relatedEventsByType = eventList.events;
		}

		// Combine related events by genre and type
		const relatedEvents = [...relatedEventsByGenre, ...relatedEventsByType];

		const datoResponse = await queryFromDato<
			ProgrammeDetailResponse & ProgrammeSpacePreviewResponse
		>({
			query: programmeDetail,
			variables: {
				spaceTitle: event.venues[0] || '',
			},
		});

		if (!datoResponse) {
			return {
				notFound: true,
			};
		}

		return {
			props: {
				programme: datoResponse.programme,
				event,
				relatedEvents,
				space: datoResponse.space,
			},
		};
	},
);

export default EventDetailPage;
